import { type ReactNode } from 'react'
import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import { type StageDwellSegment } from '@/services/loans'
import type { PropertyProfile } from '@/types/propertyProfile'
import { StageTimeline } from '@/components/deal/StageTimeline'
import { StageAuditPanel } from '@/components/deal/StageAuditPanel'
import { NarrativesPanel } from '@/components/deal/NarrativesPanel'
import { DossierPanel, EmptyLine, EditableSlot } from './ui'
import { useDossierEdit } from './editContext'
import {
  formatMoney,
  formatMultiple,
  formatPct,
  isPresent,
} from './format'

export function FinancialsPage({ raw }: { raw: RawDealFile; defs: DealFieldDef[] }) {
  const { effectiveValue } = useDossierEdit()
  const g = (name: string) => effectiveValue(name)
  const rows: { name: string; label: string; value: string; kind: 'text' | 'number' | 'percent' | 'currency' }[] = [
    { name: 'loan_amount', label: 'Loan amount', value: formatMoney(g('loan_amount')), kind: 'currency' },
    { name: 'property_value', label: 'Property value', value: formatMoney(g('property_value')), kind: 'currency' },
    { name: 'purchase_price', label: 'Purchase price', value: formatMoney(g('purchase_price')), kind: 'currency' },
    { name: 'ltv', label: 'LTV', value: formatPct(g('ltv')), kind: 'percent' },
    { name: 'ltc', label: 'LTC', value: formatPct(g('ltc')), kind: 'percent' },
    { name: 'dscr', label: 'DSCR', value: formatMultiple(g('dscr')), kind: 'number' },
    { name: 'debt_yield', label: 'Debt yield', value: formatPct(g('debt_yield')), kind: 'percent' },
    { name: 'noi', label: 'In-place NOI', value: formatMoney(g('noi')), kind: 'currency' },
    { name: 'noi_stabilized', label: 'Stabilized NOI', value: formatMoney(g('noi_stabilized')), kind: 'currency' },
    { name: 'gross_revenue', label: 'Gross revenue', value: formatMoney(g('gross_revenue')), kind: 'currency' },
    { name: 'operating_expenses', label: 'Operating expenses', value: formatMoney(g('operating_expenses')), kind: 'currency' },
    { name: 'expense_ratio', label: 'Expense ratio', value: formatPct(g('expense_ratio')), kind: 'percent' },
    // A single-tenant lease's own stated rent — never gross_revenue (owner
    // decision #7, round 6): the property's income statement and one lease's
    // rent are different figures, even on a single-tenant asset.
    { name: 'base_rent_annual', label: 'Base rent (annual)', value: formatMoney(g('base_rent_annual')), kind: 'currency' },
    { name: 'rent_per_sf', label: 'Rent / SF', value: formatMoney(g('rent_per_sf')), kind: 'currency' },
    { name: 'ncf', label: 'Net cash flow', value: formatMoney(g('ncf')), kind: 'currency' },
    { name: 'cap_rate', label: 'Cap rate', value: formatPct(g('cap_rate')), kind: 'percent' },
    { name: 'rate', label: 'Rate', value: isPresent(g('rate')) ? String(g('rate')) : '—', kind: 'text' },
    { name: 'spread', label: 'Spread', value: isPresent(g('spread')) ? String(g('spread')) : '—', kind: 'text' },
    { name: 'term_months', label: 'Term (months)', value: isPresent(g('term_months')) ? String(g('term_months')) : '—', kind: 'number' },
    { name: 'maturity_date', label: 'Maturity', value: isPresent(g('maturity_date')) ? String(g('maturity_date')) : '—', kind: 'text' },
  ]
  const cash = (raw.cash_flows ?? {}) as Record<string, unknown>
  const historic = typeof cash.historicNOI === 'number' ? cash.historicNOI : undefined
  const prospective = typeof cash.prospectiveNOI === 'number' ? cash.prospectiveNOI : undefined
  const hasNoiPair =
    historic !== undefined && prospective !== undefined && (historic !== 0 || prospective !== 0)
  const maxNoi = hasNoiPair ? Math.max(Math.abs(historic), Math.abs(prospective)) : 0

  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-4">
        {rows.map((r) => (
          <EditableSlot key={r.name} name={r.name} label={r.label} display={r.value} kind={r.kind} />
        ))}
      </div>
      <DossierPanel title="NOI — historic vs prospective">
        {hasNoiPair ? (
          <div className="space-y-3">
            {(
              [
                ['Historic NOI', historic],
                ['Prospective NOI', prospective],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <div className="mb-1 flex justify-between text-[11px] text-stone-400">
                  <span>{label}</span>
                  <span className="font-mono text-stone-200">{formatMoney(value)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded bg-white/[0.04]">
                  <div
                    className="h-full bg-amber-400/70"
                    style={{ width: `${maxNoi > 0 ? Math.round((Math.abs(value) / maxNoi) * 100) : 0}%` }}
                  />
                </div>
              </div>
            ))}
            <p className="text-[10px] text-stone-600">
              Only the two NOI figures stored on this loan. No extra years are modelled.
            </p>
          </div>
        ) : (
          <EmptyLine>No period series on this record.</EmptyLine>
        )}
      </DossierPanel>
      <p className="text-[10px] text-stone-600">
        Only fields stored on this loan are shown. Empty cells mean the figure was never recorded — not a modelled estimate.
        Unlock to edit in the header to fill them.
      </p>
    </div>
  )
}

type RentRow = {
  tenant: string
  suite: string
  sf: string
  rentMo: string
  leaseEnd: string
  status: string
}

function rentRowsFrom(raw: RawDealFile, draft: Record<string, unknown>): RentRow[] {
  const source = draft.rent_roll ?? raw.rent_roll
  if (!Array.isArray(source)) return []
  return source.map((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>
    return {
      tenant: String(row.tenant ?? row.name ?? ''),
      suite: String(row.suite ?? row.unit ?? ''),
      sf: row.sf == null || row.sf === '' ? '' : String(row.sf),
      rentMo:
        row.rentMo == null && row.monthlyRent == null && row.rent == null
          ? ''
          : String(row.rentMo ?? row.monthlyRent ?? row.rent),
      leaseEnd: String(row.leaseEnd ?? row.expiration ?? ''),
      status: String(row.status ?? ''),
    }
  })
}

function persistRentRows(rows: RentRow[]) {
  return rows.map((row) => ({
    tenant: row.tenant,
    suite: row.suite,
    sf: row.sf === '' ? null : Number(row.sf),
    monthlyRent: row.rentMo === '' ? null : Number(row.rentMo),
    rentMo: row.rentMo === '' ? null : Number(row.rentMo),
    leaseEnd: row.leaseEnd,
    status: row.status,
  }))
}

const EMPTY_RENT_ROW: RentRow = {
  tenant: '',
  suite: '',
  sf: '',
  rentMo: '',
  leaseEnd: '',
  status: '',
}

export function RentRollPage({
  profile,
  raw,
}: {
  profile: PropertyProfile
  raw: RawDealFile
  defs: DealFieldDef[]
}) {
  const edit = useDossierEdit()
  const rows = rentRowsFrom(raw, edit.draft)
  const tenants = profile.tenants ?? []

  function setRow(index: number, patch: Partial<RentRow>) {
    const fromProfile = tenants.map((t) => ({
      tenant: t.tenant,
      suite: t.suite,
      sf: t.sf ? String(t.sf) : '',
      rentMo: t.rentMo ? String(t.rentMo) : '',
      leaseEnd: t.leaseEnd,
      status: t.status,
    }))
    const base = rows.length > 0 ? rows : fromProfile.length > 0 ? fromProfile : [EMPTY_RENT_ROW]
    const next = [...base]
    while (next.length <= index) next.push({ ...EMPTY_RENT_ROW })
    next[index] = { ...next[index], ...patch }
    edit.setField('rent_roll', persistRentRows(next))
  }

  return (
    <div className="space-y-3">
      <DossierPanel title="Rent roll">
        {!edit.unlocked && tenants.length === 0 ? (
          <EmptyLine>No rent roll on this record.</EmptyLine>
        ) : edit.unlocked ? (
          <div className="space-y-2">
            <table className="w-full text-left text-[12px]">
              <thead className="text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="pb-2 font-normal">Tenant</th>
                  <th className="pb-2 font-normal">Suite</th>
                  <th className="pb-2 font-normal">SF</th>
                  <th className="pb-2 font-normal">Rent/Mo</th>
                  <th className="pb-2 font-normal">Lease end</th>
                  <th className="pb-2 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {(rows.length > 0 ? rows : [EMPTY_RENT_ROW]).map((row, index) => (
                  <tr key={index} className="border-t border-white/[0.04]">
                    {(
                      [
                        ['tenant', row.tenant],
                        ['suite', row.suite],
                        ['sf', row.sf],
                        ['rentMo', row.rentMo],
                        ['leaseEnd', row.leaseEnd],
                        ['status', row.status],
                      ] as const
                    ).map(([key, value]) => (
                      <td key={key} className="py-1 pr-1">
                        <input
                          value={value}
                          onChange={(e) => setRow(index, { [key]: e.target.value })}
                          placeholder="—"
                          className="w-full rounded border border-amber-300/30 bg-black/50 px-1.5 py-1 text-[12px] text-stone-100 placeholder:text-stone-600 focus:outline-none"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={() =>
                edit.setField(
                  'rent_roll',
                  persistRentRows([...(rows.length > 0 ? rows : [EMPTY_RENT_ROW]), EMPTY_RENT_ROW]),
                )
              }
              className="text-[11px] text-amber-300 hover:text-amber-200"
            >
              Add tenant row
            </button>
          </div>
        ) : (
          <table className="w-full text-left text-[12px]">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="pb-2 font-normal">Tenant</th>
                <th className="pb-2 font-normal">Suite</th>
                <th className="pb-2 text-right font-normal">SF</th>
                <th className="pb-2 text-right font-normal">Rent/Mo</th>
                <th className="pb-2 font-normal">Lease end</th>
                <th className="pb-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={`${t.tenant}-${t.suite}`} className="border-t border-white/[0.04] text-stone-300">
                  <td className="py-1.5">{t.tenant}</td>
                  <td>{t.suite}</td>
                  <td className="text-right font-mono">{t.sf ? t.sf.toLocaleString() : '—'}</td>
                  <td className="text-right font-mono">{t.rentMo ? `$${t.rentMo.toLocaleString()}` : '—'}</td>
                  <td>{t.leaseEnd}</td>
                  <td>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DossierPanel>
      <DossierPanel title="Lease expiration">
        {profile.leaseExpirations.length === 0 ? (
          <>
            <EmptyLine>No lease expiration schedule on this record.</EmptyLine>
            {edit.unlocked && (
              <p className="text-center text-[10px] text-stone-600">
                No column to store a derived expiration schedule yet. Record lease ends on each tenant row.
              </p>
            )}
          </>
        ) : (
          <ul className="space-y-1 text-[12px] text-stone-300">
            {profile.leaseExpirations.map((le) => (
              <li key={le.year} className="flex justify-between">
                <span>{le.year}</span>
                <span className="font-mono text-stone-500">
                  {le.pctOfTotal}% · {le.sf.toLocaleString()} SF
                </span>
              </li>
            ))}
          </ul>
        )}
      </DossierPanel>
    </div>
  )
}

/**
 * The deal's workflow: where it has been, what was recorded at each stage, and
 * the credit-box screen. Shown as Underwriting > Model Review > Workflow.
 *
 * Laid out to one screen (the owner's rule, at 1600x950 and 1366x768): the
 * three panels used to stack to 3,900px — a 46-event stage record among them —
 * and scrolled as a page through a 309px slot. Now the timeline is a strip
 * over the stage record it selects (the two belong together: picking a stage
 * opens its record), the screen sits beside them at the same height, and each
 * long list scrolls inside its own panel. The timeline carries its own
 * "Stage timeline" heading, so it has no panel header above it (no double
 * header). Below xl the three stack and the view scrolls, as before.
 */
export function ProcessPage({
  loanId,
  auditSegment,
  onSelectSegment,
  screening,
}: {
  loanId: string
  auditSegment: number | null
  onSelectSegment: (i: number) => void
  screening: ReactNode
}) {
  return (
    <div className="grid grid-cols-1 gap-2 xl:h-full xl:min-h-0 xl:grid-cols-12" data-testid="workflow-view">
      <div className="flex min-w-0 flex-col gap-2 xl:col-span-8 xl:min-h-0">
        <section
          aria-label="This deal’s stage timeline"
          className="dossier-panel shrink-0 rounded-lg border border-amber-400/15 bg-black/30 px-2"
        >
          <StageTimeline loanId={loanId} onSelectSegment={onSelectSegment} compact />
        </section>
        <DossierPanel title="Stage record" dense className="xl:min-h-0 xl:flex-1">
          <StageAuditPanel loanId={loanId} selectedIndex={auditSegment} onSelect={onSelectSegment} />
        </DossierPanel>
      </div>
      <DossierPanel title="Screening" dense className="xl:col-span-4">{screening}</DossierPanel>
    </div>
  )
}

export function NotesPage({ loanId }: { loanId: string }) {
  const { unlocked } = useDossierEdit()
  return (
    <div className="flex h-full min-h-0 flex-col">
      <NarrativesPanel dealId={loanId} readOnly={!unlocked} />
    </div>
  )
}

export type { StageDwellSegment }
